/**
 * Ref Command Autocomplete Component
 * Dropdown for @ref command - opens Reference Library panel or searches standards
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { searchApi } from '../services/api';
import { logger } from '../utils/logger';
import { useAutocompleteKeyboard } from '../hooks/useAutocompleteKeyboard';

/**
 * Extract standard number from snippet text
 * Looks for patterns like "ARP4761", "DO-178C", "AS9100D", etc.
 */
function extractStandardFromSnippet(snippet?: string): string | null {
  if (!snippet) return null;
  
  // Common standard patterns:
  // - ARP followed by numbers (ARP4761, ARP4754)
  // - DO- followed by numbers and optional letter (DO-178C, DO-254)
  // - AS followed by numbers and optional letter (AS9100D)
  // - RTCA/DO- pattern
  // - MIL-STD- pattern
  // - SAE patterns
  const patterns = [
    /\b(ARP\s*\d+[A-Z]?)\b/i,           // ARP4761, ARP 4754A
    /\b(DO-?\d+[A-Z]?)\b/i,              // DO-178C, DO178C
    /\b(AS\d+[A-Z]?)\b/i,                // AS9100D
    /\b(MIL-STD-\d+[A-Z]?)\b/i,          // MIL-STD-882
    /\b(ED-\d+[A-Z]?)\b/i,               // ED-12C (EASA equivalent)
    /\b(RTCA\/DO-?\d+[A-Z]?)\b/i,        // RTCA/DO-178C
    /\b(SAE\s+[A-Z]+\s*\d+[A-Z]?)\b/i,   // SAE ARP4761
    /\b(AC\s*\d+-\d+[A-Z]?)\b/i,         // AC 20-115D (Advisory Circulars)
  ];
  
  for (const pattern of patterns) {
    const match = snippet.match(pattern);
    if (match) {
      // Clean up the match (remove extra spaces, normalize)
      return match[1].replace(/\s+/g, '').toUpperCase();
    }
  }
  
  return null;
}

interface SearchResult {
  id: string;
  documentId?: string;
  title: string;
  standardNumber?: string;
  type: 'standard' | 'company';
  score: number;
  snippet?: string;
}

interface RefCommandAutocompleteProps {
  query: string;
  onOpenPanel: () => void; // Opens Reference Library panel
  onSelectDocument: (doc: { 
    id: string; 
    documentNumber?: string;
    title: string;
    type: 'standard' | 'company';
  }) => void;
  onClose: () => void;
  position?: { top: number; left: number };
}

export default function RefCommandAutocomplete({
  query,
  onOpenPanel,
  onSelectDocument,
  onClose,
  position,
}: RefCommandAutocompleteProps) {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced search
  useEffect(() => {
    if (!query || query.trim().length === 0) {
      setResults([]);
      return;
    }

    setLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await searchApi.searchAllDocuments(query.trim(), 8);
        interface SearchResultItem {
          id?: string;
          documentId?: string;
          title?: string;
          documentNumber?: string;
          standardNumber?: string;
          type?: string;
          score?: number;
          snippet?: string;
        }
        const filtered = (response.results || [])
          .filter((r: SearchResultItem) => r.type === 'standard' || r.type === 'company')
          .map((r: SearchResultItem) => ({
            id: r.id || r.documentId,
            documentId: r.documentId,
            title: r.title,
            standardNumber: r.documentNumber || r.standardNumber,
            type: r.type,
            score: r.score,
            snippet: r.snippet,
          }));
        setResults(filtered);
      } catch (error) {
        logger.error('Search failed:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query]);

  // All options: "Open Reference Library" + search results
  const allOptions = useMemo(() => [
    { type: 'action' as const, label: 'Open Reference Library', icon: '📚' },
    ...results.map(r => ({ type: 'result' as const, ...r })),
  ], [results]);

  const handleKeySelect = useCallback((index: number) => {
    const selected = allOptions[index];
    if (!selected) return;
    if (selected.type === 'action') {
      onOpenPanel();
    } else if ('id' in selected && selected.id) {
      const resultIndex = index - 1;
      const result = results[resultIndex];
      const standardNum = result?.standardNumber || extractStandardFromSnippet(result?.snippet);
      const displayTitle = standardNum 
        ? (result?.title && !result.title.startsWith('ref_') 
            ? `${standardNum} - ${result.title}` 
            : standardNum)
        : (result?.title && !result.title.startsWith('ref_') 
            ? result.title 
            : 'Reference Document');
      onSelectDocument({
        id: selected.id,
        documentNumber: selected.id,
        title: displayTitle,
        type: result?.type || 'standard',
      });
    }
  }, [allOptions, results, onOpenPanel, onSelectDocument]);

  const { selectedIndex } = useAutocompleteKeyboard({
    itemCount: allOptions.length,
    onSelect: handleKeySelect,
    onClose,
    wrapAround: false,
    scrollIntoView: false,
    resetDeps: [query],
  });

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={containerRef}
      className="absolute z-50 w-[450px] max-h-[350px] overflow-y-auto bg-card border border-border rounded-lg shadow-xl"
      // eslint-disable-next-line no-restricted-syntax -- Dynamic position based on prop
      style={position ? { bottom: position.top, left: position.left } : { bottom: '100%', left: 0, marginBottom: '8px' }}
    >
      {/* Header */}
      <div className="sticky top-0 bg-secondary text-muted-foreground-foreground px-4 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className="text-lg">📚</span>
          <span className="font-medium">Reference Library</span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Open the library or search for specific standards
        </p>
      </div>

      {/* Open Reference Library Option */}
      <div
        onClick={() => onOpenPanel()}
        className={`px-4 py-3 cursor-pointer flex items-center gap-3 border-b border-border ${
          selectedIndex === 0
            ? 'bg-secondary'
            : 'hover:bg-muted'
        }`}
      >
        <span className="text-2xl">📚</span>
        <div className="flex-1">
          <div className="font-medium text-foreground">
            Open Reference Library
          </div>
          <div className="text-xs text-muted-foreground">
            Browse all standards, guidance, and specifications
          </div>
        </div>
        <span className="text-xs px-2 py-0.5 bg-secondary text-secondary-foreground rounded">
          Panel
        </span>
      </div>

      {/* Search Results */}
      {query && (
        <div>
          <div className="px-3 py-1.5 bg-muted text-xs font-medium text-muted-foreground uppercase tracking-wide">
            🔍 Search Results {loading && '...'}
          </div>
          {results.length > 0 ? (
            results.map((result, index) => {
              const actualIndex = index + 1; // +1 for "Open Reference Library"
              return (
                <div
                  key={result.id}
                  onClick={() => {
                    // Try to get standard number from metadata or extract from snippet
                    const standardNum = result.standardNumber || extractStandardFromSnippet(result.snippet);
                    // Build a proper display title
                    const displayTitle = standardNum 
                      ? (result.title && !result.title.startsWith('ref_') 
                          ? `${standardNum} - ${result.title}` 
                          : standardNum)
                      : (result.title && !result.title.startsWith('ref_') 
                          ? result.title 
                          : 'Reference Document');
                    onSelectDocument({
                      id: result.id,
                      documentNumber: result.id, // Use ID for getPdfUrl API call
                      title: displayTitle,
                      type: result.type,
                    });
                  }}
                  className={`px-4 py-2 cursor-pointer flex items-center gap-3 ${
                    actualIndex === selectedIndex
                      ? 'bg-secondary'
                      : 'hover:bg-muted'
                  }`}
                >
                  <span className="text-lg">
                    {result.type === 'standard' ? '📋' : '🏢'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground truncate">
                      {/* Show standardNumber as primary, or try to extract from snippet */}
                      {result.standardNumber || extractStandardFromSnippet(result.snippet) || (result.title && !result.title.startsWith('ref_') ? result.title : 'Reference Document')}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      {/* Show snippet info */}
                      {result.snippet && (
                        <span className="truncate">{result.snippet}</span>
                      )}
                    </div>
                  </div>
                  {result.score >= 0.8 && (
                    <span className="text-xs px-1.5 py-0.5 bg-success-light text-success rounded">
                      High match
                    </span>
                  )}
                </div>
              );
            })
          ) : !loading && query.length > 0 ? (
            <div className="px-4 py-3 text-sm text-muted-foreground">
              No standards found for "{query}"
            </div>
          ) : null}
        </div>
      )}

      {/* Empty state when no query */}
      {!query && (
        <div className="px-4 py-4 text-center text-muted-foreground text-sm">
          <p>Type to search standards, or press Enter to open the library</p>
        </div>
      )}

      {/* Footer hint */}
      <div className="sticky bottom-0 px-3 py-2 bg-muted border-t border-border text-xs text-muted-foreground">
        <kbd className="px-1.5 py-0.5 bg-muted rounded">↑↓</kbd> Navigate
        <kbd className="px-1.5 py-0.5 bg-muted rounded ml-2">Enter</kbd> Select
        <kbd className="px-1.5 py-0.5 bg-muted rounded ml-2">Esc</kbd> Cancel
      </div>
    </div>
  );
}
